import { useRef, useEffect, useCallback, useState } from 'react'
import { ScrollArea } from '@/components/ui'
import { Message } from './Message'
import { TypingIndicator } from './TypingIndicator'
import { WelcomeScreen } from './WelcomeScreen'
import { cn } from '@/utils/cn'
import { messageText } from '@/types'
import type { Message as MessageType, WidgetActionHandler } from '@/types'

export interface MessageListProps {
  messages: MessageType[]
  isLoading?: boolean
  onWidgetAction?: WidgetActionHandler
  onVote?: (messageId: string, wasHelpful: boolean | null) => void
  isVoting?: boolean
  onSuggestionSelect?: (text: string) => void
  suggestedChipsEnabled?: boolean
  careAdvocateCallbackSchedulingEnabled?: boolean
  className?: string
}

/**
 * Scrollable message list with auto-scroll behavior and accessibility features.
 * Uses role="log" and aria-live for screen reader announcements.
 */
export function MessageList({
  messages,
  isLoading = false,
  onWidgetAction,
  onVote,
  isVoting,
  onSuggestionSelect,
  suggestedChipsEnabled = false,
  careAdvocateCallbackSchedulingEnabled = false,
  className,
}: MessageListProps) {
  const scrollRef = useRef<HTMLDivElement>(null)
  const bottomRef = useRef<HTMLDivElement>(null)

  // The actively-streaming bubble: last agent text message not yet finalized
  // (no serverId) while a response is in flight AND emitted at least one
  // text_delta (streaming-governor signature). Buffered turns emit only
  // text/widget events and must not animate — their answer arrives whole at
  // `complete`, so a typewriter reveal would fake latency that already passed.
  const streamingMessage = isLoading
    ? [...messages].reverse().find(
        (m) =>
          m.sender === 'agent' &&
          m.type === 'text' &&
          !m.serverId &&
          m.streamed === true,
      )
    : undefined
  const streamingId = streamingMessage?.id
  // Streamed deltas grow an existing bubble without changing messages.length,
  // so the auto-scroll effect below also keys on the live bubble's content
  // size — otherwise a long governed answer grows below the fold with no
  // follow. Segment count is included so a widget arriving mid-stream (which
  // adds no text) still triggers the follow.
  const streamingTextLength =
    streamingMessage && streamingMessage.type === 'text'
      ? messageText(streamingMessage.segments).length +
        streamingMessage.segments.length
      : 0
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(true)
  const [showScrollButton, setShowScrollButton] = useState(false)

  // Check if user has scrolled up
  const handleScroll = useCallback(() => {
    const container = scrollRef.current
    if (!container) return

    const { scrollTop, scrollHeight, clientHeight } = container
    const distanceFromBottom = scrollHeight - scrollTop - clientHeight
    const isNearBottom = distanceFromBottom < 100

    setIsAutoScrollEnabled(isNearBottom)
    setShowScrollButton(!isNearBottom && messages.length > 0)
  }, [messages.length])

  // Auto-scroll to bottom when new messages arrive OR the live streamed bubble
  // grows (streamingTextLength changes on every text_delta).
  useEffect(() => {
    if (isAutoScrollEnabled && bottomRef.current) {
      bottomRef.current.scrollIntoView({ behavior: 'smooth' })
    }
  }, [messages.length, isLoading, isAutoScrollEnabled, streamingTextLength])

  // Scroll to bottom button handler
  const scrollToBottom = useCallback(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    setIsAutoScrollEnabled(true)
  }, [])

  // Attach scroll listener
  useEffect(() => {
    const container = scrollRef.current
    if (!container) return

    container.addEventListener('scroll', handleScroll, { passive: true })
    return () => container.removeEventListener('scroll', handleScroll)
  }, [handleScroll])

  return (
    <div className={cn('relative flex-1', className)}>
      <ScrollArea className="h-full" dir="ltr">
        <div
          ref={scrollRef}
          className="h-full overflow-y-auto px-4 py-4 relative"
          role="log"
          aria-live="polite"
          aria-label="Chat messages"
          aria-relevant="additions"
        >
          {messages.length === 0 && !isLoading && suggestedChipsEnabled && onSuggestionSelect && (
            <WelcomeScreen
              onSelect={onSuggestionSelect}
              careAdvocateCallbackSchedulingEnabled={careAdvocateCallbackSchedulingEnabled}
            />
          )}

          {messages.length === 0 && !isLoading && !(suggestedChipsEnabled && onSuggestionSelect) && (
            <div className="flex h-full items-center justify-center">
              <p className="text-center text-gray-500">
                No messages yet. Start a conversation!
              </p>
            </div>
          )}

          <div className="space-y-4" role="list">
            {messages.map((message) => (
              <div key={message.id} className="group" style={{ contentVisibility: 'auto', containIntrinsicSize: '0 80px' }}>
                <Message
                  message={message}
                  onWidgetAction={onWidgetAction}
                  onVote={onVote}
                  isVoting={isVoting}
                  onSuggestionSelect={onSuggestionSelect}
                  suggestionsDisabled={isLoading}
                  suggestedChipsEnabled={suggestedChipsEnabled}
                  animate={message.id === streamingId}
                />
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <TypingIndicator />
              </div>
            )}
          </div>

          {/* Scroll anchor */}
          <div ref={bottomRef} aria-hidden="true" />
        </div>
      </ScrollArea>

      {/* Scroll to bottom button */}
      {showScrollButton && (
        <button
          type="button"
          onClick={scrollToBottom}
          className={cn(
            'absolute bottom-4 left-1/2 -translate-x-1/2',
            'flex items-center gap-2 rounded-full',
            'bg-gray-800 px-4 py-2 text-sm text-white shadow-lg',
            'hover:bg-gray-700 focus:outline-none focus:ring-2',
            'focus:ring-primary-500 focus:ring-offset-2',
            'transition-all'
          )}
          aria-label="Scroll to latest messages"
        >
          <span aria-hidden="true">{'\u2193'}</span>
          New messages
        </button>
      )}
    </div>
  )
}
